export type LiveResponseMode =
  | 'respond_immediately'
  | 'listen_only'
  | 'respond_on_direct_address'
  | 'respond_on_visual_trigger'

export type UserAffect = 'neutral' | 'positive' | 'frustrated' | 'stressed' | 'excited'
export type UserEnergy = 'low' | 'medium' | 'high'
export type PreferredPace = 'slow' | 'normal' | 'fast'
export type PreferredVerbosity = 'terse' | 'balanced' | 'detailed'

export interface LiveAffectiveState {
  affect: UserAffect
  energy: UserEnergy
  preferredPace: PreferredPace
  preferredVerbosity: PreferredVerbosity
  confidence?: number
  source?: 'default' | 'text' | 'voice' | 'manual' | 'system'
  updatedAt?: string
}

export interface LiveSessionPolicy {
  languageCode: string
  responseMode: LiveResponseMode
  affectiveState: LiveAffectiveState
  visualAttentionEnabled: boolean
  proactiveAudioEnabled: boolean
  updatedAt?: string
}

export type LiveSessionPolicyOverrides = Partial<
  Omit<LiveSessionPolicy, 'affectiveState'>
> & {
  affectiveState?: Partial<LiveAffectiveState>
}

export const DEFAULT_LIVE_LANGUAGE_CODE = 'en-US'

export const DEFAULT_LIVE_AFFECTIVE_STATE: LiveAffectiveState = {
  affect: 'neutral',
  energy: 'medium',
  preferredPace: 'normal',
  preferredVerbosity: 'balanced',
  confidence: 0.5,
  source: 'default',
}

export const DEFAULT_LIVE_SESSION_POLICY: LiveSessionPolicy = {
  languageCode: DEFAULT_LIVE_LANGUAGE_CODE,
  responseMode: 'respond_immediately',
  affectiveState: DEFAULT_LIVE_AFFECTIVE_STATE,
  visualAttentionEnabled: true,
  proactiveAudioEnabled: true,
}

export function createDefaultLiveAffectiveState(
  overrides?: Partial<LiveAffectiveState>
): LiveAffectiveState {
  return {
    ...DEFAULT_LIVE_AFFECTIVE_STATE,
    ...(overrides || {}),
  }
}

export function createDefaultLiveSessionPolicy(
  overrides?: LiveSessionPolicyOverrides
): LiveSessionPolicy {
  return {
    ...DEFAULT_LIVE_SESSION_POLICY,
    ...(overrides || {}),
    affectiveState: createDefaultLiveAffectiveState({
      ...DEFAULT_LIVE_SESSION_POLICY.affectiveState,
      ...(overrides?.affectiveState || {}),
    }),
  }
}

export function mergeLiveSessionPolicy(
  current?: LiveSessionPolicyOverrides,
  patch?: LiveSessionPolicyOverrides
): LiveSessionPolicy {
  const base = createDefaultLiveSessionPolicy(current)
  if (!patch) return base

  return createDefaultLiveSessionPolicy({
    ...base,
    ...patch,
    affectiveState: {
      ...base.affectiveState,
      ...(patch.affectiveState || {}),
    },
  })
}
